import { useState, useEffect } from 'react';
import {
  requestForgotPasswordNonLoginNew,
  requestForgotPasswordNonLoginConfirm,
  requestForgotPasswordNonLoginOTP,
  requestForgotPasswordNonLoginOTPVerify,
} from 'lib/api/change-forgot-password';
import { CHANNEL_OTP } from '../config';
import { maskEmail } from '../utils';

interface Password {
  new: string;
  confirm: string;
}

interface PasswordValidation {
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  characters: boolean;
}

export interface OTPData {
  target: string;
  remainingSecond: number;
}

export interface OTPConfig {
  channel: string;
  otp: string;
}

export const useForgotPasswordNonLogin = (): {
  email: string;
  setEmail: (e: string) => void;
  password: Password;
  setPassword: (password: Password) => void;
  passwordValidation: PasswordValidation;
  setOtpConfig: (otpConfig: OTPConfig) => void;
  otpConfig: OTPConfig;
  otpData: OTPData;
  submitNewPassword: (cb: () => void) => Promise<void>;
  submitConfirmPassword: (cb: () => void) => Promise<void>;
  verifyOTP: (cb: () => void) => Promise<void>;
  requestOTP: (cb?: () => void) => Promise<void>;
  isSubmitting: boolean;
  error: string;
  resetError: () => void;
} => {
  const [token, setToken] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');

  const [password, setPassword] = useState<Password>({
    new: '',
    confirm: '',
  });
  const [passwordValidation, setPasswordValidation] =
    useState<PasswordValidation>({
      uppercase: false,
      lowercase: false,
      number: false,
      characters: false,
    });
  const [otpData, setOtpData] = useState<OTPData>({
    target: '',
    remainingSecond: 0,
  });
  const [otpConfig, setOtpConfig] = useState<OTPConfig>({
    channel: '',
    otp: '',
  });

  const submitNewPassword = async (cb: () => void) => {
    try {
      setIsSubmitting(true);
      await requestForgotPasswordNonLoginNew({
        password: password.new,
        token,
      });
      setError('');
      cb();
    } catch (e) {
      setError(e?.cause?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitConfirmPassword = async (cb: () => void) => {
    try {
      setIsSubmitting(true);
      await requestForgotPasswordNonLoginConfirm({
        password: password.confirm,
        token,
      });
      setError('');
      cb();
    } catch (e) {
      setError(e?.cause?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const requestOTP = async (cb?: () => void) => {
    try {
      setIsSubmitting(true);
      const response = await requestForgotPasswordNonLoginOTP(email);

      // Calculate remaining scond to next OTP attempt
      const givenTime = new Date(response?.data?.data?.next_attempt_time);
      const currentTime = new Date();
      const timeDifference = givenTime.getTime() - currentTime.getTime();
      const remainingSecond = Math.floor(timeDifference / 1000);
      /// ///////////

      setToken(response?.data?.data?.token);
      setOtpData({
        target: maskEmail(email),
        remainingSecond,
      });
      setOtpConfig({
        channel: CHANNEL_OTP.EMAIL,
        otp: '',
      });
      if (cb !== undefined) {
        cb();
      }
    } catch (err) {
      setOtpData({
        target: otpData.target,
        remainingSecond: 0,
      });
      setError(err?.cause?.message);
      // Re-throw error so component can handle
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const verifyOTP = async (cb: () => void) => {
    try {
      setIsSubmitting(true);
      await requestForgotPasswordNonLoginOTPVerify({
        otp: otpConfig.otp,
        token,
      });
      setError('');
      cb();
    } catch (e) {
      setError(e?.cause?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    setPasswordValidation({
      uppercase: /[A-Z]/.test(password.new),
      lowercase: /[a-z]/.test(password.new),
      number: /\d/.test(password.new),
      characters: password.new?.length >= 8,
    });
  }, [password.new]);

  useEffect(() => {
    setError('');
  }, [password]);

  return {
    email,
    setEmail,
    password,
    setPassword,
    passwordValidation,
    setOtpConfig,
    otpConfig,
    otpData,
    submitNewPassword,
    submitConfirmPassword,
    requestOTP,
    verifyOTP,
    isSubmitting,
    error,
    resetError: () => setError(''),
  };
};
